import React, { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CheckoutAnalytics from './CheckoutAnalytics';

// Importante: não importa nada do componente original para não criar dependência direta

/**
 * Componente wrapper que adiciona análise avançada sem modificar os componentes originais
 * Funciona através de referência ao DOM e monitoramento de eventos
 */
const AnalyticsWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  // Determinar se estamos na página de checkout
  const isCheckoutPage = location.pathname.includes('/checkout');
  
  // Extrair ID da oferta da URL se disponível
  const getOfferIdFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('offerId') || undefined;
  };
  
  useEffect(() => {
    // Setup para interceptar callbacks sem modificar arquivos originais
    if (isCheckoutPage && wrapperRef.current) {
      // Procurar elementos que precisamos monitorar
      const checkoutComponent = wrapperRef.current.querySelector('.checkout-form-container');
      
      if (checkoutComponent) {
        // Adicionar data attributes para rastreamento
        const totalAmountElements = checkoutComponent.querySelectorAll('.checkout-total-price, .total-price');
        totalAmountElements.forEach(element => {
          // Extrai apenas os dígitos e pontos para obter o valor numérico
          const priceText = element.textContent || '';
          const numericValue = priceText.replace(/[^\d.,]/g, '').replace(',', '.');
          
          if (numericValue) {
            element.setAttribute('data-total-amount', numericValue);
          }
        });
        
        // Adicionar atributos aos checkboxes de bumps para rastreamento
        const bumpCheckboxes = checkoutComponent.querySelectorAll('input[type="checkbox"][id^="bump-"]');
        bumpCheckboxes.forEach(checkbox => {
          const checkboxId = checkbox.getAttribute('id');
          const bumpId = checkboxId ? checkboxId.replace('bump-', '') : 'unknown';
          checkbox.setAttribute('data-bump-id', bumpId);
          
          // Tentar encontrar o preço associado ao bump
          const priceElement = checkbox.closest('.bump-container')?.querySelector('.bump-price');
          if (priceElement) {
            const priceText = priceElement.textContent || '';
            const numericValue = priceText.replace(/[^\d.,]/g, '').replace(',', '.');
            checkbox.setAttribute('data-bump-price', numericValue);
          }
        });
        
        // Adicionar atributos para cupons
        const couponElement = checkoutComponent.querySelector('.coupon-applied');
        if (couponElement) {
          couponElement.setAttribute('data-coupon-applied', 'true');
          
          // Tentar encontrar o valor do desconto
          const discountElement = checkoutComponent.querySelector('.discount-value');
          if (discountElement) {
            const discountText = discountElement.textContent || '';
            const numericValue = discountText.replace(/[^\d.,]/g, '').replace(',', '.');
            couponElement.setAttribute('data-coupon-discount', numericValue);
          }
        }
      }
    }
  }, [isCheckoutPage, location]);

  return (
    <div ref={wrapperRef}>
      {children}
      
      {isCheckoutPage && wrapperRef.current && (
        <CheckoutAnalytics
          checkoutRef={wrapperRef as React.RefObject<HTMLDivElement>}
          offerId={getOfferIdFromUrl()}
        />
      )}
    </div>
  );
};

export default AnalyticsWrapper;
